import React, { useState } from 'react';
import LgmButton from './LgmButton';
import TeCodeValue from './TeCodeValue';
import TeCodeHeaderTooltip from './TeCodeHeaderTooltip';
import ApplTypeValue from './ApplTypeValue';
import ApplTypeHeaderTooltip from './ApplTypeHeaderTooltip';
import ObTypeValue from './ObTypeValue';
import ObTypeHeaderTooltip from './ObTypeHeaderTooltip';
import DmfStatusHeaderTooltip from './DmfStatusHeaderTooltip';
import DmfTypeHeaderTooltip from './DmfTypeHeaderTooltip';
import {
  getFdaSourceSummaryFields,
  getFdaSourceDetailFields,
  getFdaSourceRowTitle,
  getFdaSourceTitleFieldKey,
  resolveFdaSourceFieldValue,
  resolveFdaSourceFieldRawValue,
} from '../domains/products/adapters/fdaSourceSearchFieldConfig';
import {
  FdaSourceKey,
  FdaSourceRow,
} from '../services/api/domains/products/types';
import { GRAY_200, GRAY_50, GRAY_500, GRAY_900 } from 'theme/grayScale';

interface FdaSourceResultRowProps {
  source: FdaSourceKey;
  row: FdaSourceRow;
}

/**
 * One FDA Sources search result row — compact summary by default,
 * expandable in place to full detail (docs/features/fda-source-product-search.md,
 * Task FSS-007, Sub-task 7.1).
 *
 * Extracted from `ExternalLinkReviewList.tsx`'s row + expand-toggle
 * pattern (Architecture Decision 11) with all write/staging logic removed
 * — no checkboxes, no stage-to-link state, no `is_linked` badge, no
 * remove action. `ExternalLinkReviewList.tsx` itself is unmodified.
 */
const FdaSourceResultRow = ({ source, row }: FdaSourceResultRowProps) => {
  const [expanded, setExpanded] = useState(false);
  // The title already shows whichever field getFdaSourceRowTitle actually
  // used (the primary field, or its fallback when the primary is
  // null/empty) — exclude that specific field, by key, from the inline
  // summary row below so it isn't rendered twice.
  const titleFieldKey = getFdaSourceTitleFieldKey(source, row);
  const summaryFields = getFdaSourceSummaryFields(source).filter(
    field => field.key !== titleFieldKey
  );
  const detailFields = getFdaSourceDetailFields(source);
  const title = getFdaSourceRowTitle(source, row);

  return (
    <li style={styles.row}>
      <div style={styles.rowHeader}>
        <div style={styles.rowContent}>
          <span style={styles.rowTitle}>{title}</span>
          <div style={styles.rowSummary}>
            {summaryFields.map(field => (
              <span key={field.key} style={styles.summaryField}>
                <strong>{field.label}:</strong>{' '}
                {resolveFdaSourceFieldValue(row, field)}
              </span>
            ))}
          </div>
        </div>
        <div style={styles.rowActions}>
          <LgmButton
            variant="flat"
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
            {expanded ? 'Hide details' : 'View details'}
          </LgmButton>
        </div>
      </div>

      {expanded && detailFields.length > 0 && (
        <div style={styles.details}>
          {detailFields.map(field => (
            <div key={field.key} style={styles.detailRow}>
              <span style={styles.detailLabel}>
                {field.label}
                {field.key === 'te_code' && <TeCodeHeaderTooltip />}
                {field.key === 'appl_type' && <ApplTypeHeaderTooltip />}
                {field.key === 'type' && <ObTypeHeaderTooltip />}
                {field.key === 'status' && <DmfStatusHeaderTooltip />}
                {field.key === 'dmf_type' && <DmfTypeHeaderTooltip />}
              </span>
              <span style={styles.detailValue}>
                {field.key === 'te_code' ? (
                  <TeCodeValue
                    code={resolveFdaSourceFieldRawValue(row, field)}
                  />
                ) : field.key === 'appl_type' ? (
                  <ApplTypeValue
                    code={resolveFdaSourceFieldRawValue(row, field)}
                  />
                ) : field.key === 'type' ? (
                  <ObTypeValue
                    code={resolveFdaSourceFieldRawValue(row, field)}
                  />
                ) : (
                  resolveFdaSourceFieldValue(row, field)
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </li>
  );
};

const styles: Record<string, React.CSSProperties> = {
  row: {
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    backgroundColor: GRAY_50,
    padding: '0.5rem',
    listStyle: 'none',
  },
  rowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  rowContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    minWidth: 0,
  },
  rowTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: GRAY_900,
  },
  rowSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    fontSize: '0.75rem',
    color: GRAY_500,
  },
  summaryField: {
    whiteSpace: 'nowrap',
  },
  rowActions: {
    display: 'flex',
    gap: '0.25rem',
    flexShrink: 0,
  },
  details: {
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: `1px solid ${GRAY_200}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailRow: {
    display: 'flex',
    gap: '0.5rem',
    fontSize: '0.8125rem',
  },
  detailLabel: {
    minWidth: '9rem',
    fontWeight: 600,
    color: GRAY_500,
    flexShrink: 0,
  },
  detailValue: {
    color: GRAY_900,
    wordBreak: 'break-word',
  },
};

export default FdaSourceResultRow;
